import {
  getCountryCallingCode,
  PhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/mobile';
import { CountryCode as CountryCodeLib } from 'libphonenumber-js/types';

export type CountryCode = CountryCodeLib;

export interface PhoneSupportedCountryCodes {
  name: string;
  isoCode: CountryCode;
}

/* correspondence table can be found here: https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2 */
const PHONE_SUPPORTED_COUNTRY_ISO_CODES: CountryCode[] = [
  'FR',
  'GP',
  'RE',
  'MQ',
  'YT',
  'NC',
  'PF',
  'GF',
];

const PHONE_SUPPORTED_CALLING_CODES = new Set<string>(
  PHONE_SUPPORTED_COUNTRY_ISO_CODES.map((isoCode) => getCountryCallingCode(isoCode)),
);

/**
 * libphonenumber only uses the selected country for numbers written in national format: a number
 * typed with an explicit "+CC" prefix keeps its own country whatever the dropdown says. So we check
 * the country the number resolves to, not the one that was selected.
 * The calling code is the fallback when the country is ambiguous (e.g. shared +262 range).
 */
function isSupportedPhoneNumber(parsed: PhoneNumber): boolean {
  if (parsed.country) {
    return PHONE_SUPPORTED_COUNTRY_ISO_CODES.includes(parsed.country);
  }
  return PHONE_SUPPORTED_CALLING_CODES.has(parsed.countryCallingCode);
}

export type CellPhoneValidationError = 'invalid' | 'unsupported_country';

/**
 * Tells why a phone is rejected: 'unsupported_country' when it is a well-formed number from a
 * country we don't accept (e.g. +49...), 'invalid' for anything else. Undefined when it is fine.
 */
export function getCellPhoneValidationError(
  phoneNumber: string,
  countryCode: CountryCode,
  options: { allowBlank?: boolean; strict?: boolean } = { allowBlank: true, strict: true },
): CellPhoneValidationError | undefined {
  if (phoneNumber.length === 0 && options.allowBlank) {
    return undefined;
  }
  const parsed = parsePhoneNumberFromString(phoneNumber, countryCode);
  if (!parsed) {
    return 'invalid';
  }
  if (!isSupportedPhoneNumber(parsed)) {
    return 'unsupported_country';
  }
  return (options.strict ? parsed.isValid() : parsed.isPossible()) ? undefined : 'invalid';
}

export function validateCellPhone(
  phoneNumber: string,
  countryCode: CountryCode,
  options: { allowBlank?: boolean; strict?: boolean } = { allowBlank: true, strict: true },
): boolean {
  return !getCellPhoneValidationError(phoneNumber, countryCode, options);
}
